import { Link } from "react-router-dom";
import { ArrowUpRight, Pill } from "lucide-react";
import { KMD } from "../../data";
import { Reveal } from "./Reveal";

export const ATTStrip = () => (
  <section data-testid="att-strip" className="relative py-16 md:py-20 border-y border-white/10" style={{ background: "linear-gradient(90deg, rgba(0,168,224,0.16) 0%, #0a192f 70%)" }}>
    <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12">
      <Reveal className="shrink-0">
        <img
          src="/att/heritage-att-banner.png"
          alt="Heritage Family Protection Agency — AT&T Authorized Dealer"
          data-testid="att-strip-banner"
          className="w-full max-w-sm lg:max-w-md rounded-2xl border border-[#00A8E0]/25"
        />
      </Reveal>
      <Reveal delay={0.06} className="flex-1">
        <h3 className="font-serif-display text-2xl md:text-3xl text-white">
          We are now an <span className="italic" style={{ color: "#00A8E0" }}>AT&amp;T Authorized Dealer.</span>
        </h3>
        <p className="mt-2 font-body font-light text-sm md:text-base text-white/60 max-w-2xl">
          Heritage Family Protection Agency is now an AT&amp;T Authorized Dealer. Looking to get residential or
          business wireless or internet services? Explore the latest phones, unlimited plans, and AT&amp;T Fiber —
          and pre-order right through us.
        </p>
      </Reveal>
      <Reveal delay={0.12} className="shrink-0">
        <Link
          to="/att"
          data-testid="att-strip-cta"
          className="btn-sweep inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-body text-sm font-semibold tracking-wide text-white transition-transform duration-300 hover:scale-[1.03]"
          style={{ backgroundColor: "#00A8E0" }}
        >
          Click Here <ArrowUpRight size={15} />
        </Link>
      </Reveal>
    </div>
  </section>
);

export const IPhone18Strip = () => (
  <section data-testid="iphone18-home-strip" className="relative py-14 md:py-20">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl border border-white/10 shadow-[0_25px_80px_rgba(0,0,0,0.45)]">
          <img
            src="/att/iphone18-hero.jpg"
            alt="Apple iPhone 18 Pro and Pro Max in four colors held in hand"
            data-testid="iphone18-home-image"
            className="w-full h-[52vh] md:h-[68vh] object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#081226] via-[#081226]/30 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#081226]/70 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-7 md:p-12">
            <p className="font-body text-xs md:text-sm font-bold tracking-[0.3em] uppercase" style={{ color: "#00A8E0" }}>
              Now Available
            </p>
            <h2 className="mt-3 max-w-2xl font-serif-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white leading-[1.08]">
              Apple iPhone 18 Pro &amp; Pro Max — <span className="text-gold-gradient italic">now available with Heritage.</span>
            </h2>
            <p className="mt-3 max-w-xl font-body font-light text-sm md:text-base text-white/70 leading-relaxed">
              Pre-order through Heritage Family Protection Agency, your AT&amp;T Authorized Dealer — pick your color,
              storage, and installment plan, and trade in for promo pricing.
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <Link
                to="/att/devices"
                data-testid="iphone18-home-cta"
                className="btn-sweep inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-body text-sm font-semibold tracking-wide text-white"
                style={{ backgroundColor: "#00A8E0" }}
              >
                Pre-Order Yours <ArrowUpRight size={15} />
              </Link>
              <Link
                to="/att"
                data-testid="iphone18-home-learn"
                className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 font-body text-sm font-semibold tracking-wide text-white hover:border-[#00A8E0] hover:text-[#00A8E0] transition-colors duration-300"
              >
                Explore AT&amp;T Services
              </Link>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export const MedsStrip = () => (
  <section data-testid="meds-strip" className="relative py-16 md:py-20 border-y border-white/10" style={{ background: "linear-gradient(90deg, rgba(29,110,199,0.16) 0%, #0a192f 70%)" }}>
    <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12">
      <Reveal className="shrink-0">
        <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl border border-[#4d9fe8]/40 bg-[#4d9fe8]/10 text-[#4d9fe8]">
          <Pill size={30} strokeWidth={1.4} />
        </span>
      </Reveal>
      <Reveal delay={0.06} className="flex-1">
        <span className="inline-flex items-center gap-2 rounded-full border border-[#d4af37]/40 bg-[#d4af37]/10 px-4 py-1.5 font-body text-[10px] font-bold tracking-[0.25em] uppercase text-[#d4af37]" data-testid="meds-strip-badge">
          Coming Soon
        </span>
        <h3 className="mt-4 font-serif-display text-2xl md:text-3xl lg:text-4xl text-white">
          Your medications, <span className="italic" style={{ color: "#4d9fe8" }}>delivered to your door.</span>
        </h3>
        <p className="mt-3 font-body font-light text-sm md:text-base text-white/60 max-w-2xl leading-relaxed">
          Heritage will soon be delivering medications directly to your home — bringing the pharmacy to your doorstep
          with the same care we bring to protecting your family. Stay tuned; the announcement is coming right here.
        </p>
      </Reveal>
      <Reveal delay={0.12} className="shrink-0">
        <Link
          to="/contact"
          data-testid="meds-strip-cta"
          className="inline-flex items-center gap-2 rounded-full border border-[#4d9fe8]/50 px-7 py-3.5 font-body text-sm font-semibold tracking-wide text-white hover:border-[#4d9fe8] hover:text-[#4d9fe8] transition-colors duration-300"
        >
          Be the First to Know <ArrowUpRight size={15} />
        </Link>
      </Reveal>
    </div>
  </section>
);

export const CommunityStrip = () => (
  <section data-testid="community-strip" className="relative py-16 md:py-24 border-y border-white/10" style={{ background: "linear-gradient(90deg, rgba(122,28,49,0.20) 0%, #0a192f 65%)" }}>
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
      <Reveal className="lg:col-span-4">
        <Link to="/community" className="block max-w-[280px] mx-auto lg:mx-0">
          <div className="rounded-2xl border-2 border-[#d4af37]/60 p-1.5 bg-[#081226] shadow-[0_0_45px_rgba(212,175,55,0.18)] transition-transform duration-300 hover:scale-[1.02]">
            <img
              src="/community/lets-talk-give-back-flyer.png"
              alt="Let's Talk & Give Back community event flyer — September 26, 2026 at Stone Terrace, Stonecrest GA"
              data-testid="community-strip-flyer"
              className="rounded-xl w-full h-auto"
              loading="lazy"
            />
          </div>
        </Link>
      </Reveal>
      <div className="lg:col-span-8">
        <Reveal delay={0.06}>
          <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase text-[#d4af37]">Community Bulletin</p>
          <h3 className="mt-3 font-serif-display text-3xl md:text-4xl lg:text-5xl text-white">
            We show up for our community — <span className="text-gold-gradient italic">come see us in person.</span>
          </h3>
          <p className="mt-4 max-w-2xl font-body font-light text-base md:text-lg text-white/65 leading-relaxed">
            Heritage Family Protection Agency is a proud community partner at <strong className="font-semibold text-white">Let's Talk &amp; Give Back — Clothing. Connections &amp; Community.</strong> Join
            us Saturday, September 26, 2026 from 11:00 AM to 3:00 PM at Stone Terrace, 6659 Chupp Rd, Stonecrest, GA 30038.
          </p>
          <div className="mt-7 flex flex-wrap gap-4">
            <Link
              to="/community"
              data-testid="community-strip-cta"
              className="btn-sweep inline-flex items-center gap-2 rounded-full bg-gold-metallic px-7 py-3.5 font-body text-sm font-semibold tracking-wide text-black"
            >
              View the Community Bulletin <ArrowUpRight size={15} />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const KonnectStrip = () => (
  <section data-testid="konnectmd-strip" className="relative py-16 md:py-20 border-y border-white/10" style={{ background: "linear-gradient(90deg, rgba(17,115,185,0.14) 0%, #0a192f 70%)" }}>
    <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12">
      <Reveal className="shrink-0">
        <div className="inline-flex items-center rounded-xl bg-white px-5 py-3">
          <img src={KMD.logo} alt="KonnectMD" className="h-9 md:h-10 w-auto object-contain" />
        </div>
      </Reveal>
      <Reveal delay={0.06} className="flex-1">
        <h3 className="font-serif-display text-2xl md:text-3xl text-white">
          24/7 virtual healthcare — <span className="italic" style={{ color: "#6db6e8" }}>no insurance required.</span>
        </h3>
        <p className="mt-2 font-body font-light text-sm md:text-base text-white/60 max-w-2xl">
          Talk to a doctor anytime, get 1,000+ common medications free, and pay $0 copays.
          Plans from $59.99/month cover your partner and kids under 26.
        </p>
      </Reveal>
      <Reveal delay={0.12} className="shrink-0">
        <Link
          to="/konnectmd"
          data-testid="konnectmd-strip-cta"
          className="btn-sweep inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-body text-sm font-semibold tracking-wide text-white transition-transform duration-300 hover:scale-[1.03]"
          style={{ backgroundColor: KMD.blue }}
        >
          Explore KonnectMD <ArrowUpRight size={15} />
        </Link>
      </Reveal>
    </div>
  </section>
);
